import { findMindmapNode, type MindmapView, type WorkflowView } from '@blueprint/domain'
import { useEffect, useState } from 'react'
import { RepositoryError } from '../../data/index.js'
import { getRepository, useBoardStore } from '../../state/board-store.js'
import { useMindmapStore } from '../../state/mindmap-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { useWorkflowStore } from '../../state/workflow-store.js'
import styles from './SourceLinks.module.css'

/**
 * Links from a generated storymap back to its mindmap or workflow. References only: the
 * mindmap is read to name the node, and a node or mindmap that has gone is shown as removed.
 */

type Source = { state: 'loading' } | { state: 'ready'; mindmap: MindmapView } | { state: 'gone' } | { state: 'error' }

/** One read per mindmap per board open, shared by every link on the page. */
const cache = new Map<string, Promise<MindmapView | null>>()

function loadMindmap(id: string): Promise<MindmapView | null> {
  let pending = cache.get(id)
  if (!pending) {
    pending = getRepository()
      .getMindmap(id)
      .catch((error: unknown) => {
        if (error instanceof RepositoryError && error.kind === 'not_found') return null
        cache.delete(id)
        throw error
      })
    cache.set(id, pending)
  }
  return pending
}

function useSourceMindmap(id: string | undefined): Source {
  const [source, setSource] = useState<Source>({ state: 'loading' })
  useEffect(() => {
    if (!id) return
    let cancelled = false
    setSource({ state: 'loading' })
    loadMindmap(id)
      .then((mindmap) => !cancelled && setSource(mindmap ? { state: 'ready', mindmap } : { state: 'gone' }))
      .catch(() => !cancelled && setSource({ state: 'error' }))
    return () => {
      cancelled = true
    }
  }, [id])
  return source
}

/** The same for a board's workflow: read once per board open, to name it. */
type WorkflowSource = { state: 'loading' } | { state: 'ready'; workflow: WorkflowView } | { state: 'gone' } | { state: 'error' }

const workflowCache = new Map<string, Promise<WorkflowView | null>>()

function loadWorkflow(id: string): Promise<WorkflowView | null> {
  let pending = workflowCache.get(id)
  if (!pending) {
    pending = getRepository()
      .getWorkflow(id)
      .catch((error: unknown) => {
        if (error instanceof RepositoryError && error.kind === 'not_found') return null
        workflowCache.delete(id)
        throw error
      })
    workflowCache.set(id, pending)
  }
  return pending
}

function useSourceWorkflow(id: string | undefined): WorkflowSource {
  const [source, setSource] = useState<WorkflowSource>({ state: 'loading' })
  useEffect(() => {
    if (!id) return
    let cancelled = false
    setSource({ state: 'loading' })
    loadWorkflow(id)
      .then((workflow) => !cancelled && setSource(workflow ? { state: 'ready', workflow } : { state: 'gone' }))
      .catch(() => !cancelled && setSource({ state: 'error' }))
    return () => {
      cancelled = true
    }
  }, [id])
  return source
}

/** Forget what was read, e.g. when the board changes, so a renamed node shows its new text. */
export function forgetSourceMindmaps(): void {
  cache.clear()
  workflowCache.clear()
}

async function openNode(mindmapId: string, nodeId: string): Promise<void> {
  useUiStore.getState().closeEditor()
  await useMindmapStore.getState().open(mindmapId, nodeId)
  useUiStore.getState().setView('mindmap')
}

async function openWorkflow(workflowId: string, nodeId?: string): Promise<void> {
  useUiStore.getState().closeEditor()
  await useWorkflowStore.getState().open(workflowId, nodeId)
  useUiStore.getState().setView('workflow')
}

/** In the board's top bar: which mindmap branch, or which workflow, this board was generated from. */
export function BoardSourceLink() {
  const source = useBoardStore((state) => state.entities?.board.source)
  const boardId = useBoardStore((state) => state.activeBoardId)
  useEffect(() => forgetSourceMindmaps(), [boardId])
  const mindmap = useSourceMindmap(source?.mindmapId)
  const workflow = useSourceWorkflow(source?.workflowId)
  if (source?.workflowId) {
    const { workflowId } = source
    if (workflow.state === 'loading' || workflow.state === 'error') return null
    if (workflow.state === 'gone') {
      return <span className={styles.removed} title="The workflow this board was generated from has been deleted">From a deleted workflow</span>
    }
    return (
      <button
        type="button"
        className={styles.link}
        data-testid="board-source-link"
        title="Open the workflow this board was generated from"
        onClick={() => void openWorkflow(workflowId)}
      >
        <span className={styles.label}>From workflow</span> {workflow.workflow.name}
      </button>
    )
  }
  // A board from a workflow has no mindmap branch to link to.
  if (!source?.mindmapId || !source.nodeId) return null
  const { mindmapId, nodeId } = source
  if (mindmap.state === 'loading' || mindmap.state === 'error') return null
  if (mindmap.state === 'gone') {
    return <span className={styles.removed} title="The mindmap this board was generated from has been deleted">From a deleted mindmap</span>
  }
  const node = findMindmapNode(mindmap.mindmap.root, nodeId)
  return (
    <button
      type="button"
      className={styles.link}
      data-testid="board-source-link"
      title="Open the mindmap branch this board was generated from"
      onClick={() => void openNode(mindmapId, nodeId)}
    >
      <span className={styles.label}>From mindmap</span> {mindmap.mindmap.name}
      {node && node.id !== mindmap.mindmap.root.id ? ` › ${node.title}` : ''}
      {node ? '' : ' (source removed)'}
    </button>
  )
}

/** In the card editor: the mindmap or workflow node the card came from, and its current text when it has drifted. */
export function CardSourceLink({ nodeId, cardTitle }: { nodeId: string | undefined; cardTitle: string }) {
  const mindmapId = useBoardStore((state) => state.entities?.board.source?.mindmapId)
  const mindmap = useSourceMindmap(nodeId ? mindmapId : undefined)
  const workflowId = useBoardStore((state) => state.entities?.board.source?.workflowId)
  const workflow = useSourceWorkflow(nodeId ? workflowId : undefined)
  if (nodeId && workflowId) {
    if (workflow.state === 'loading' || workflow.state === 'error') return null
    const workflowNode = workflow.state === 'ready' ? workflow.workflow.graph.nodes.find((candidate) => candidate.id === nodeId) : undefined
    if (!workflowNode) {
      return (
        <span className={styles.removed} data-testid="card-source-link">
          Workflow node: source removed
        </span>
      )
    }
    return (
      <button
        type="button"
        className={styles.link}
        data-testid="card-source-link"
        title="Open the workflow node this card came from"
        onClick={() => void openWorkflow(workflowId, nodeId)}
      >
        <span className={styles.label}>From workflow node</span> {workflowNode.title}
        {workflowNode.title !== cardTitle ? <span className={styles.drift}> (node text differs from the card)</span> : null}
      </button>
    )
  }
  if (!nodeId || !mindmapId || mindmap.state === 'loading' || mindmap.state === 'error') return null
  const node = mindmap.state === 'ready' ? findMindmapNode(mindmap.mindmap.root, nodeId) : null
  if (!node) {
    return (
      <span className={styles.removed} data-testid="card-source-link">
        Mindmap node: source removed
      </span>
    )
  }
  return (
    <button
      type="button"
      className={styles.link}
      data-testid="card-source-link"
      title="Open the mindmap node this card came from"
      onClick={() => void openNode(mindmapId, nodeId)}
    >
      <span className={styles.label}>From mindmap node</span> {node.title}
      {node.title !== cardTitle ? <span className={styles.drift}> (node text differs from the card)</span> : null}
    </button>
  )
}
